import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { AddToCollectionModalStore } from "@/components/modal/add-to-collection/add-to-collection-modal-store";
import { useModalTitleLimit } from "@/components/modal/modal-title-limits/modal-title-limits";
import {
  MDSModalDialog,
  MDSDialogSlotName,
  MDSText,
  MDSTextVariant,
  MDSTextField,
  MDSTextFieldSlotName,
  MDSTextFieldVariant,
  MDSIcon,
  MDSIconKind,
  MDSMenuDropdownPopover,
  MDSSelectMenuWithContent,
  MDSDialogChip,
  MDSDialogChipVariant,
} from "@/mds";
import styled from "@emotion/styled";
import { useHotkey } from "@tanstack/react-hotkeys";
import { resolveHotkey } from "@/modules/keyboard-shortcuts/platform";
import { KEYBOARD_SHORTCUT_BINDINGS } from "@/modules/keyboard-shortcuts/registry";

interface AddToCollectionModalProps {
  store: AddToCollectionModalStore;
}

export const AddToCollectionModal = observer<AddToCollectionModalProps>(({ store }) => {
  const primaryTitle = store.selectedNote?.title || UNTITLED_NOTE_TITLE;
  const secondaryTitle =
    store.selectedNoteIds.length > 1
      ? ` and ${store.selectedNoteIds.length - 1} other note${store.selectedNoteIds.length > 2 ? "s" : ""} `
      : " ";

  const title = useModalTitleLimit({ primaryTitle, secondaryTitle, isAllShared: false });
  useEffect(() => {
    if (store.isOpen) {
      store.setSearchQuery("");
    }
  }, [secondaryTitle, store.isOpen, title, store]);

  return (
    <MDSModalDialog isOpen={store.isOpen} handleClose={() => store.close()}>
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        Organize <MDSDialogChip inline label={title} variant={MDSDialogChipVariant.Note} />{" "}
        {secondaryTitle}
      </MDSText>
      <div slot={MDSDialogSlotName.Content}>
        <CollectionsSearchField store={store} />
      </div>
    </MDSModalDialog>
  );
});

const CollectionsSearchField = observer<AddToCollectionModalProps>(({ store }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => inputRef.current?.focus(), []);
  const inputHotkeyOptions = { enabled: store.isOpen, target: inputRef };
  const menuHotkeyOptions = {
    enabled: store.isOpen && store.dropdownContentList.length > 0,
    target: menuRef,
  };

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.focus_next_result),
    () => {
      menuRef.current?.focus();
    },
    inputHotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.focus_previous_result),
    () => {
      menuRef.current?.focus();
    },
    inputHotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.escape_to_close),
    () => {
      if (store.searchQuery.length > 0) store.setSearchQuery("");
      else if (isMenuOpen) menuRef.current?.focus();
      else store.close();
    },
    inputHotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.escape_to_close),
    () => {
      inputRef.current?.focus();
    },
    menuHotkeyOptions
  );

  return (
    <>
      <CollectionSearchField ref={triggerRef}>
        <MDSTextField
          variant={MDSTextFieldVariant.Form}
          ref={inputRef}
          value={store.searchQuery}
          onChange={value => store.setSearchQuery(value)}
          placeholder="Add to existing or new collection"
        >
          <MDSIcon slot={MDSTextFieldSlotName.LeftAddon} kind={MDSIconKind.Search} />
        </MDSTextField>
        <div>
          <StyledMenuDropdownPopover
            triggerRef={triggerRef}
            isOpen={store.dropdownContentList.length > 0}
            placement="bottom start"
            isNonModal
            onOpenChange={setIsMenuOpen}
          >
            <StyledSelectMenu
              selectionBehavior="toggle"
              selectionMode="single"
              shouldFocusWrap={true}
              contentList={store.dropdownContentList}
              ref={menuRef}
            />
          </StyledMenuDropdownPopover>
        </div>
      </CollectionSearchField>
    </>
  );
});

const StyledMenuDropdownPopover = styled(MDSMenuDropdownPopover)`
  width: min(600px, calc(90vw - 40px));
  max-width: unset;
`;

const StyledSelectMenu = styled(MDSSelectMenuWithContent)`
  width: 100%;
  max-width: unset;
`;

const CollectionSearchField = styled.div`
  position: relative;
`;
